import React, { useState } from "react";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import SettingsLayout from "./SettingsLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";

export default function SecuritySettings() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const changePassword = async () => {
    if (!current || !next || !confirm) {
      toast.error("Please fill in all fields.");
      return;
    }
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords don't match.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const sendReset = async () => {
    setSending(true);
    try {
      await api.post("/auth/forgot-password", { email: user?.email });
      toast.success("If that email exists, a reset link has been sent.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <SettingsLayout title="Security" subtitle="Manage your password" testId="security-settings-page">
      <div className="space-y-5">
        <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
          <p className="text-sm font-semibold text-foreground flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-primary" /> Change password
          </p>
          <div>
            <Label htmlFor="cur-pw" className="text-sm font-medium text-foreground">Current password</Label>
            <Input id="cur-pw" data-testid="current-password-input" type="password" value={current}
              onChange={(e) => setCurrent(e.target.value)} className="mt-1.5 h-12 rounded-xl" autoComplete="current-password" />
          </div>
          <div>
            <Label htmlFor="new-pw" className="text-sm font-medium text-foreground">New password</Label>
            <Input id="new-pw" data-testid="new-password-input" type="password" value={next}
              onChange={(e) => setNext(e.target.value)} className="mt-1.5 h-12 rounded-xl" autoComplete="new-password" />
          </div>
          <div>
            <Label htmlFor="conf-pw" className="text-sm font-medium text-foreground">Confirm new password</Label>
            <Input id="conf-pw" data-testid="confirm-password-input" type="password" value={confirm}
              onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 h-12 rounded-xl" autoComplete="new-password" />
          </div>
          <Button data-testid="change-password-button" onClick={changePassword} disabled={saving}
            className="w-full h-12 rounded-xl text-base">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {saving ? "Saving…" : "Update password"}
          </Button>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <p className="text-sm font-semibold text-foreground">Forgot your password?</p>
          <p className="mt-1 text-xs text-muted-foreground">
            We'll email a secure reset link to <span className="font-medium text-foreground">{user?.email}</span>.
          </p>
          <Button data-testid="send-reset-button" variant="outline" onClick={sendReset} disabled={sending}
            className="mt-3 w-full h-11 rounded-xl">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {sending ? "Sending…" : "Send reset link"}
          </Button>
        </div>
      </div>
    </SettingsLayout>
  );
}
